import React, { useState, useEffect, useCallback } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import Layout from '../../components/ui/Layout';
import Table from '../../components/ui/Table';
// @ts-ignore - ActionDropdown component exists but may have type issues
import ActionDropdown from '../../components/ui/ActionDropdown';
import ActionButtonsRow from '../../components/ui/ActionButtonsRow';
import SearchFilterCard, { FilterConfig } from '../../components/ui/SearchFilterCard';
import BulkActionsBar from '../../components/ui/BulkActionsBar';
import { RiEditLine, RiDeleteBinLine, RiEyeLine, RiTruckLine, RiToggleLine } from 'react-icons/ri';
import supplierService, { Supplier } from '../../services/supplierService';
import { showAlert, showConfirm } from '../../utils/alert';
import { getApiErrorMessageAsync } from '../../utils/mapFieldErrors';
import { queryKeys } from '../../queryClient';
import { useDeleteSupplier, useToggleSupplierStatus } from '../../hooks/mutations/useSupplierMutations';
import { PAGINATION_CONFIG } from '../../config/pagination';
import Pagination from '../../components/ui/Pagination';
import { useUserPageSize } from '../../hooks/useUserPageSize';
import { formatDateTimeFull, getLocalDateString } from '../../utils/dateUtils';
import { downloadExcelFile } from '../../utils/downloadExcel';
import { usePermission } from '../../hooks/usePermission';
import LoadingOverlayModal from '../../components/ui/LoadingOverlayModal';
import { useClampPage } from '../../hooks/useClampPage';
import { runBulkDelete, bulkDeleteFailureMessage } from '../../utils/bulkDelete';
import { detailNavState } from '../../components/detail/detailNavigation';
import { SUPPLIER_NEW_PATH, supplierDetailUrl, supplierEditUrl } from '../../utils/supplierLinks';

const SupplierPage: React.FC = () => {
  const { canCreate, canEdit, canDelete, canExport } = usePermission();
  // ดู/เพิ่ม/แก้ไข = หน้าเต็ม (มติ PO 28 ต.ค. 2026 · เดิมโมดัล) — แนบ URL รายการ (ตัวกรอง/หน้า) ไปเป็น from
  const navigate = useNavigate();
  const location = useLocation();
  const openSupplierPage = (path: string) => navigate(path, { state: detailNavState(location) });
  // S1111 URL-sync: read URL params for lazy init (filter+page bidirectional)
  const [searchParams, setSearchParams] = useSearchParams();
  const urlPageParam = searchParams.get('page');
  const urlStatusParam = searchParams.get('status') || '';
  // State สำหรับการค้นหาและ pagination
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [currentPage, setCurrentPage] = useState<number>(() => {
    if (!urlPageParam) return 1;
    const n = parseInt(urlPageParam, 10);
    if (!Number.isFinite(n) || n < 1) return 1;
    return Math.min(n, 9999);
  });
  const [pageSize, setPageSize] = useState<number>(PAGINATION_CONFIG.DEFAULT_PAGE_SIZE);
  useUserPageSize(pageSize, setPageSize);

  // State สำหรับ filter
  // S1111 URL-sync: hydrate filterStatus from URL
  const [filterStatus, setFilterStatus] = useState<string>(
    urlStatusParam === 'active' || urlStatusParam === 'inactive' ? urlStatusParam : ''
  );

  // S1111 URL-sync: write filter+page to URL (Variant A overwrite — no other URL params on this page)
  const syncFiltersToUrl = useCallback((status: string, page: number) => {
    const newParams = new URLSearchParams();
    if (status === 'active' || status === 'inactive') newParams.set('status', status);
    if (page > 1) newParams.set('page', String(page));
    setSearchParams(newParams, { replace: true });
  }, [setSearchParams]);

  useEffect(() => {
    syncFiltersToUrl(filterStatus, currentPage);
  }, [filterStatus, currentPage, syncFiltersToUrl]);

  // S1111 + BUG-S771-PAGERESET (S771): filter/search → page reset.
  // เดิม watch เฉพาะ filterStatus แต่ search box wire onSearchChange={setSearchTerm} ตรงๆ (bypass handleSearch
  // ที่ reset page) → พิมพ์ค้นหาตอนอยู่หน้าลึก ไม่ reset → paged slice out-of-range → "ไม่มีข้อมูล" ทั้งที่มีผล
  // (bug class เดียวกับ DevicePage). ขยาย ref ให้ครอบ searchTerm ด้วย (composite key · mount-safe).
  const prevFilterKeyRef = React.useRef<string>(`${filterStatus}|${searchTerm}`);
  useEffect(() => {
    const key = `${filterStatus}|${searchTerm}`;
    if (prevFilterKeyRef.current !== key) {
      prevFilterKeyRef.current = key;
      setCurrentPage(1);
    }
  }, [filterStatus, searchTerm]);

  const deleteSupplierMutation = useDeleteSupplier();
  const toggleStatusMutation = useToggleSupplierStatus();

  const [selectedSuppliers, setSelectedSuppliers] = useState<number[]>([]);
  const [bulkLoading, setBulkLoading] = useState<boolean>(false);

  // React Query for data fetching
  const { data, isLoading, isFetching, error, refetch } = useQuery({
    queryKey: [...queryKeys.suppliers.list(), { page: currentPage, limit: pageSize, search: searchTerm, filterStatus }],
    queryFn: async () => {
      const params = {
        page: currentPage,
        limit: pageSize,
        search: searchTerm || undefined,
        is_active: filterStatus === 'active' ? true : filterStatus === 'inactive' ? false : undefined,
      };
      return await supplierService.getSuppliers(params);
    },
  });

  // ข้อมูลผู้จำหน่ายจาก React Query
  const suppliers = data?.items || [];
  const totalItems = data?.total || 0;
  useClampPage(currentPage, data?.total, pageSize, setCurrentPage, isLoading);

  // ฟังก์ชันสำหรับการเลือกผู้จำหน่ายทั้งหมด
  // KD3 S2515: เปลี่ยนตัวกรอง/หน้าแล้วต้องล้างการเลือก — ไม่งั้นแถบ "เลือกแล้ว N" ค้าง และ "ลบที่เลือก" ลบแถวที่ผู้ใช้มองไม่เห็นแล้ว
  useEffect(() => {
    setSelectedSuppliers([]);
  }, [currentPage, pageSize, searchTerm, filterStatus]);

  const handleSelectAll = (selected: boolean) => {
    if (selected) {
      setSelectedSuppliers(suppliers.map(supplier => supplier.id));
    } else {
      setSelectedSuppliers([]);
    }
  };

  // ฟังก์ชันสำหรับการเลือกผู้จำหน่ายแต่ละรายการ
  const handleSelectSupplier = (supplierId: number, selected: boolean) => {
    if (selected) {
      setSelectedSuppliers(prev => [...prev, supplierId]);
    } else {
      setSelectedSuppliers(prev => prev.filter(id => id !== supplierId));
    }
  };

  // ฟังก์ชันสำหรับการลบผู้จำหน่ายหลายรายการ
  const handleBulkDelete = async () => {
    if (selectedSuppliers.length === 0) {
      showAlert('เลือกข้อมูล', 'กรุณาเลือกผู้จำหน่ายที่ต้องการลบ', 'info');
      return;
    }

    showConfirm(
      'ยืนยันการลบผู้จำหน่าย',
      `คุณต้องการลบผู้จำหน่าย ${selectedSuppliers.length} รายการที่เลือก ใช่หรือไม่? การกระทำนี้ไม่สามารถเปลี่ยนกลับได้`,
      async () => {
        setBulkLoading(true);
        try {
          // BUG-S2103-002: allSettled — รายการที่ลบไม่ได้ต้องบอกเหตุผล · ตารางต้อง refetch · เลือกค้างเฉพาะที่ลบไม่ได้
          const result = await runBulkDelete(selectedSuppliers, id => supplierService.deleteSupplier(id));
          setSelectedSuppliers(result.failed.map(f => f.id));
          refetch();
          if (result.failed.length === 0) {
            showAlert('สำเร็จ', `ลบผู้จำหน่าย ${result.succeeded.length} รายการสำเร็จ`, 'success');
          } else {
            showAlert(result.succeeded.length ? 'ลบได้บางรายการ' : 'ลบไม่สำเร็จ', bulkDeleteFailureMessage('ผู้จำหน่าย', result), result.succeeded.length ? 'warning' : 'error');
          }
        } catch (error) {
          console.error('Error bulk deleting:', error);
          showAlert('เกิดข้อผิดพลาด', 'ไม่สามารถลบรายการที่เลือกได้', 'error');
        } finally {
          setBulkLoading(false);
        }
      },
      { isDanger: true, confirmText: 'ลบ', cancelText: 'ยกเลิก' }
    );
  };

  // Table columns configuration
  const columns = [
    {
      key: 'select',
      title: (
        <div className="flex items-center">
          <input
            type="checkbox"
            aria-label="เลือกผู้จำหน่ายทั้งหมด"
            checked={selectedSuppliers.length === suppliers.length && suppliers.length > 0}
            onChange={(e) => handleSelectAll(e.target.checked)}
            className="rounded border-secondary-300 text-primary-600 focus:ring-primary-500"
          />
        </div>
      ),
      width: '50px',
      render: (value: any, record: Supplier) => (
        <input
          type="checkbox"
          aria-label={`เลือกผู้จำหน่าย ${record.name || record.id}`}
          checked={selectedSuppliers.includes(record.id)}
          onChange={(e) => handleSelectSupplier(record.id, e.target.checked)}
          className="rounded border-secondary-300 text-primary-600 focus:ring-primary-500"
        />
      ),
    },
    {
      key: 'code',
      title: 'รหัส',
      render: (value: string) => <span className="font-mono text-sm">{value || '-'}</span>,
      sortable: true,
      width: '120px'
    },
    {
      key: 'name',
      title: 'ชื่อผู้จำหน่าย',
      render: (value: string) => <span className="font-medium">{value || '-'}</span>,
      sortable: true
    },
    {
      key: 'contact_person',
      title: 'ผู้ติดต่อ',
      render: (value: string) => <span className="text-secondary-600">{value || '-'}</span>
    },
    {
      key: 'phone',
      title: 'เบอร์โทร',
      render: (value: string) => <span className="text-secondary-600">{value || '-'}</span>
    },
    {
      key: 'email',
      title: 'อีเมล',
      render: (value: string) => <span className="text-secondary-600">{value || '-'}</span>
    },
    {
      key: 'is_active',
      title: 'สถานะ',
      render: (value: boolean) => (
        <span className={`px-2 py-1 rounded-full text-xs font-medium ${value
          ? 'bg-success-100 text-success-800'
          : 'bg-danger-100 text-danger-800'
          }`}>
          {value ? 'ใช้งาน' : 'ไม่ใช้งาน'}
        </span>
      ),
      width: '100px'
    },
    {
      key: 'created_at',
      title: 'วันที่สร้าง',
      sortable: true,
      render: (value: string | null) => formatDateTimeFull(value)
    },
    {
      key: 'actions',
      title: 'การดำเนินการ',
      sticky: true,
      render: (_: any, record: Supplier) => (
        <ActionDropdown
          actions={[
            {
              label: 'ดูรายละเอียด',
              icon: <RiEyeLine className="w-4 h-4" />,
              onClick: () => openSupplierPage(supplierDetailUrl(record.id))
            },
            canEdit('suppliers') && {
              label: 'แก้ไข',
              icon: <RiEditLine className="w-4 h-4" />,
              onClick: () => openSupplierPage(supplierEditUrl(record.id))
            },
            canEdit('suppliers') && {
              divider: true
            },
            canEdit('suppliers') && {
              label: record.is_active ? 'ปิดใช้งาน' : 'เปิดใช้งาน',
              icon: <RiToggleLine className="w-4 h-4" />,
              onClick: () => {
                showConfirm(
                  'ยืนยันการเปลี่ยนสถานะ',
                  `คุณต้องการ${record.is_active ? 'ปิด' : 'เปิด'}ใช้งานผู้จำหน่าย "${record.name}" ใช่หรือไม่?`,
                  () => {
                    toggleStatusMutation.mutate(record.id);
                  },
                  { confirmText: 'ยืนยัน', cancelText: 'ยกเลิก' }
                );
              },
              className: record.is_active ? 'text-warning-600 hover:text-warning-700 hover:bg-warning-50' : 'text-success-600 hover:text-success-700 hover:bg-success-50'
            },
            canDelete('suppliers') && {
              divider: true
            },
            canDelete('suppliers') && {
              label: 'ลบ',
              icon: <RiDeleteBinLine className="w-4 h-4" />,
              onClick: () => handleDelete(record),
              className: 'text-danger-600 hover:text-danger-700 hover:bg-danger-50'
            }
          ].filter(Boolean)}
          buttonIcon={null}
          buttonLabel=""
        />
      ),
      width: '60px'
    }
  ];


  // จัดการการค้นหา
  const handleSearch = (term: string) => {
    setSearchTerm(term);
    setCurrentPage(1);
  };

  // ฟังก์ชัน Refresh
  const handleRefresh = () => {
    setSearchTerm('');
    setCurrentPage(1);
    setFilterStatus('');
    setSelectedSuppliers([]);
    refetch();
  };

  // State สำหรับ Export Loading
  const [isExporting, setIsExporting] = useState(false);

  // ฟังก์ชัน Export Excel
  const handleExportExcel = async () => {
    setIsExporting(true);
    try {
      // ส่งตัวกรองชุดเดียวกับที่หน้ารายการใช้ — ไฟล์ต้องได้เฉพาะแถวที่เห็นบนจอ
      const blob = await supplierService.exportToExcel({
        search: searchTerm || undefined,
        is_active: filterStatus === 'active' ? true : filterStatus === 'inactive' ? false : undefined,
      });
      downloadExcelFile(blob, `suppliers_${getLocalDateString()}.xlsx`);
      showAlert('สำเร็จ', 'ส่งออกข้อมูลเรียบร้อยแล้ว', 'success');
    } catch (error) {
      console.error('Error exporting data:', error);
      showAlert('เกิดข้อผิดพลาด', await getApiErrorMessageAsync(error, 'ไม่สามารถส่งออกข้อมูลได้'), 'error');
    } finally {
      setIsExporting(false);
    }
  };

  // จัดการการลบข้อมูล
  const handleDelete = (supplier: Supplier) => {
    showConfirm(
      'ยืนยันการลบ',
      `คุณต้องการลบผู้จำหน่าย "${supplier.name}" ใช่หรือไม่? การกระทำนี้ไม่สามารถเปลี่ยนกลับได้`,
      () => {
        deleteSupplierMutation.mutate(supplier.id);
      },
      { isDanger: true, confirmText: 'ลบ', cancelText: 'ยกเลิก' }
    );
  };

  // Filter configuration สำหรับ SearchFilterCard
  const filters: FilterConfig[] = [
    {
      key: 'status',
      label: 'สถานะ',
      type: 'select',
      options: [
        { value: 'active', label: 'ใช้งาน' },
        { value: 'inactive', label: 'ไม่ใช้งาน' },
      ],
      tagColor: 'success',
    },
  ];

  return (
    <Layout>
      <div className="space-y-6">
        {/* Page Header with Executive Navy Theme */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-5">
          <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-xl bg-primary-600 flex items-center justify-center flex-shrink-0 shadow-sm">
            <RiTruckLine className="w-5 h-5 text-white" />
          </div>
          <div>
            <h1 className="text-xl font-semibold text-secondary-900 leading-tight">ซัพพลายเออร์</h1>
            <p className="text-xs text-secondary-500 mt-0.5">จัดการข้อมูลซัพพลายเออร์และผู้จำหน่าย</p>
          </div>
        </div>
        {totalItems > 0 && (
          <span className="inline-flex items-center px-3 min-h-[44px] sm:min-h-[32px] py-1.5 rounded-lg bg-secondary-100 text-secondary-600 text-xs font-medium self-start sm:self-auto">
            {totalItems.toLocaleString()} รายการ
          </span>
        )}
      </div>

        {/* Error Message from React Query */}
        {error && (
          <div className="bg-danger-50 border border-danger-300 rounded-lg p-4">
            <div className="flex items-center">
              <span className="text-danger-800">เกิดข้อผิดพลาดในการโหลดข้อมูล</span>
            </div>
          </div>
        )}

        {/* Action Buttons Row */}
        <ActionButtonsRow
          onAdd={canCreate('suppliers') ? () => openSupplierPage(SUPPLIER_NEW_PATH) : undefined}
          onRefresh={handleRefresh}
          refreshLoading={isFetching}
          onExport={canExport('suppliers') ? handleExportExcel : undefined}
          exportDisabled={!suppliers?.length}
        />

        {/* Search and Filter Card */}
        <SearchFilterCard
          searchTerm={searchTerm}
          onSearchChange={setSearchTerm}
          filters={filters}
          filterValues={{ status: filterStatus }}
          onFilterChange={(key, value) => setFilterStatus(value)}
          searchPlaceholder="ค้นหารหัสหรือชื่อผู้จำหน่าย..."
        />

        {/* Bulk Actions Bar */}
        {canDelete('suppliers') && (
          <BulkActionsBar
            selectedCount={selectedSuppliers.length}
            onBulkDelete={handleBulkDelete}
            loading={bulkLoading}
          />
        )}

        {/* Table */}
        <Table
          columns={canDelete('suppliers') ? columns : columns.filter(c => c.key !== 'select')}  /* KD3 S2122 (BUG-S2122-004): ไม่มีสิทธิ์ลบ = ไม่มีช่องติ๊ก (ติ๊กแล้วไม่มีอะไรให้ทำ) */
          data={suppliers}
          loading={isLoading}
          emptyText={error ? 'โหลดข้อมูลไม่สำเร็จ' : 'ไม่พบข้อมูลผู้จำหน่าย'}
          emptyHint={error ? null : undefined}  /* BUG-S2273-001: โหลดล้ม ≠ ตัวกรองไม่เจอ */
          pagination={false}
          searchable={false}
          actions={false}
          rowKey="id"
        />

        {/* Pagination */}
        {!isLoading && suppliers.length > 0 && (
          <Pagination
            currentPage={currentPage}
            totalItems={totalItems}
            pageSize={pageSize}
            onPageChange={setCurrentPage}
            onPageSizeChange={(newPageSize) => {
              setPageSize(newPageSize);
              setCurrentPage(1);
            }}
            showPageSizeSelector={true}
            showSummary={true}
            className="mt-5"
          />
        )}

        {/* Loading Overlay for Export */}
        <LoadingOverlayModal
          isOpen={isExporting}
          icon="export"
          title="กำลังส่งออกข้อมูล"
          submessage="กรุณารอสักครู่ ระบบกำลังเตรียมไฟล์ Excel"
        />
      </div>
    </Layout>
  );
};

export default SupplierPage;
